/**
 * LES INDICATEURS — lot 103, 18/09/2026.
 *
 * Maquette `maquette-analyse-indicateurs-v2.html`, cadrage « Analyse
 * financière : 7. les indicateurs » et « 8. les arbitrages ».
 *
 * Vingt-trois cartes, sur la dernière PÉRIODE CLOSE. Aucun objectif n'est
 * proposé : une carte sans seuil est mesurée, pas jugée. Chaque carte s'ouvre
 * sur son explication, ses douze périodes, et la saisie de ses seuils.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { FAMILLES, type Famille } from '@stafa/core';
import type { EchecApi } from './api.js';
import { enregistrerSeuils, lireIndicateurs } from './apiAnalyse.js';
import { CourbeAnalyse } from './CourbeAnalyse.js';
import {
  cartesDe,
  compteursDe,
  depuisSeuil,
  ecrire,
  filtrer,
  NOMS_STATUTS,
  periodeEnMots,
  versSeuil,
  type Carte,
  type IndicateursLus,
} from './indicateurs.js';
import { BandeauFiabilite } from './BandeauFiabilite.js';

export interface ActionsIndicateurs {
  readonly poserSeuils: (
    cle: string,
    objectif: number | null,
    alerte: number | null,
  ) => Promise<string | null>;
}

export function EcranIndicateurs({
  jeton,
  quandAccesPerdu,
  messageRefus,
  sources,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  readonly sources: (quandLu: () => void) => ReactNode;
}) {
  const [donnees, setDonnees] = useState<IndicateursLus | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  async function charger() {
    const j = await jeton();
    if (j === null) return quandAccesPerdu();
    const r = await lireIndicateurs(j);
    if (r.etat === 'ok') {
      setDonnees(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? messageRefus(r.cause));
    }
  }

  useEffect(() => {
    void charger();
  }, [jeton]);

  const actions: ActionsIndicateurs = {
    poserSeuils: async (cle, objectif, alerte) => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return 'Session terminée.';
      }
      const r = await enregistrerSeuils(j, cle, objectif, alerte);
      if (r.etat !== 'ok') {
        if (r.cause === 'acces') quandAccesPerdu();
        return r.message ?? messageRefus(r.cause);
      }
      await charger();
      return null;
    },
  };

  return (
    <VueIndicateurs
      donnees={donnees}
      refus={refus}
      sources={sources(() => void charger())}
      fiabilite={<BandeauFiabilite jeton={jeton} ecran="indicateurs" />}
      actions={actions}
    />
  );
}

export function VueIndicateurs({
  donnees,
  refus,
  sources,
  actions,
  fiabilite = null,
  familleInitiale = 'tout',
  ecartsInitial = false,
  ouverteInitiale = null,
}: {
  readonly donnees: IndicateursLus | null;
  readonly refus: string | null;
  readonly sources: ReactNode;
  readonly actions: ActionsIndicateurs;
  /** Le bandeau de fiabilite (lot 108). Nul dans l'apercu de maquette. */
  readonly fiabilite?: ReactNode;
  readonly familleInitiale?: Famille | 'tout';
  readonly ecartsInitial?: boolean;
  readonly ouverteInitiale?: string | null;
}) {
  const [famille, setFamille] = useState<Famille | 'tout'>(familleInitiale);
  const [ecarts, setEcarts] = useState(ecartsInitial);
  const [ouverte, setOuverte] = useState<string | null>(ouverteInitiale);
  const [panneau, setPanneau] = useState(false);

  const cartes = useMemo(() => (donnees === null ? [] : cartesDe(donnees)), [donnees]);
  const compte = compteursDe(cartes);
  const montrees = filtrer(cartes, famille, ecarts);

  if (donnees === null) {
    return (
      <main className="corps corps-analyse">
        {refus !== null ? (
          <p className="bandeau bandeau-refus">{refus}</p>
        ) : (
          <p className="attente">Lecture des indicateurs…</p>
        )}
      </main>
    );
  }

  return (
    <main className="corps corps-analyse">
      <div className="bfr-filtres">
        <div className="ech-champ">
          Famille
          <div className="bfr-segments" role="group" aria-label="Famille">
            <button
              type="button"
              aria-pressed={famille === 'tout'}
              onClick={() => setFamille('tout')}
            >
              Toutes
            </button>
            {FAMILLES.map((f) => (
              <button
                key={f.cle}
                type="button"
                aria-pressed={famille === f.cle}
                onClick={() => setFamille(f.cle)}
              >
                {f.nom}
              </button>
            ))}
          </div>
        </div>
        <label className="ech-bascule">
          <input
            type="checkbox"
            checked={ecarts}
            onChange={(e) => setEcarts(e.target.checked)}
          />
          Seulement ce qui ne va pas
        </label>
        <div className="bfr-outils">
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau}
            onClick={() => setPanneau(!panneau)}
          >
            Données Pennylane
          </button>
        </div>
      </div>
      <p className="ech-sous">
        Semaine close : {donnees.semaine.debut ?? '—'} → {donnees.semaine.fin} · Mois
        clos : {periodeEnMots(donnees.mois ?? '')} · Jamais la période en cours.
        {' · '}
        <strong className="ind-rouge">{compte.rouge}</strong> en alerte ·{' '}
        <strong className="ind-orange">{compte.orange}</strong> à surveiller ·{' '}
        <strong className="ind-vert">{compte.vert}</strong> dans l’objectif ·{' '}
        {compte.sans} sans objectif
      </p>
      {panneau && <div className="ech-sources">{sources}</div>}
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
      {fiabilite}

      {montrees.length === 0 ? (
        <p className="ech-vide">
          {ecarts
            ? 'Rien à signaler : aucune carte en alerte ni à surveiller.'
            : 'Aucun indicateur.'}
        </p>
      ) : (
        <div className="ind-grille">
          {montrees.map((c) => (
            <CarteIndicateur
              key={c.definition.cle}
              carte={c}
              quandOuvrir={() => setOuverte(c.definition.cle)}
            />
          ))}
        </div>
      )}

      {ouverte !== null && (
        <FenetreIndicateur
          carte={cartes.find((c) => c.definition.cle === ouverte) ?? null}
          actions={actions}
          quandFermer={() => setOuverte(null)}
        />
      )}
    </main>
  );
}

function Etincelle({ carte }: { readonly carte: Carte }) {
  const valeurs = carte.lue.serie.map((s) => s.valeur);
  const connues = valeurs.filter((v): v is number => v !== null);
  if (connues.length < 2) return null;
  const min = Math.min(...connues);
  const max = Math.max(...connues);
  const etendue = max - min || Math.abs(max) || 1;
  const points = valeurs
    .map((v, i) =>
      v === null
        ? null
        : `${((i / (valeurs.length - 1)) * 100).toFixed(1)},${(28 - ((v - min) / etendue) * 24).toFixed(1)}`,
    )
    .filter((p): p is string => p !== null);
  return (
    <svg
      className="ind-etincelle"
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline points={points.join(' ')} />
    </svg>
  );
}

function CarteIndicateur({
  carte,
  quandOuvrir,
}: {
  readonly carte: Carte;
  readonly quandOuvrir: () => void;
}) {
  const { definition: d, lue, statut, tendance } = carte;
  return (
    <button type="button" className={`ind-carte ind-${statut}`} onClick={quandOuvrir}>
      <span className="ind-nom">{d.nom}</span>
      <strong className="ind-valeur">{ecrire(lue.valeur, d.unite)}</strong>
      <span className="ind-sous">
        {lue.valeur === null
          ? (lue.manque ?? 'non calculé')
          : (lue.sous_ligne ?? periodeEnMots(lue.periode))}
      </span>
      <span className="ind-pied">
        <span
          className={`ind-tendance ${tendance.bonne === null ? '' : tendance.bonne ? 'ind-vert' : 'ind-rouge'}`}
        >
          {tendance.ecart === null
            ? '—'
            : `${tendance.ecart > 0 ? '▲' : tendance.ecart < 0 ? '▼' : '='} ${Math.abs(tendance.ecart * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
        </span>
        <span className="ech-doux">{NOMS_STATUTS[statut]}</span>
      </span>
      <Etincelle carte={carte} />
      {d.ecran !== undefined && (
        <span className="ech-etiquette ind-rappel">{d.ecran}</span>
      )}
    </button>
  );
}

function FenetreIndicateur({
  carte,
  actions,
  quandFermer,
}: {
  readonly carte: Carte | null;
  readonly actions: ActionsIndicateurs;
  readonly quandFermer: () => void;
}) {
  const [objectif, setObjectif] = useState('');
  const [alerte, setAlerte] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (carte === null) return;
    setObjectif(depuisSeuil(carte.lue.objectif, carte.definition.unite));
    setAlerte(depuisSeuil(carte.lue.alerte, carte.definition.unite));
  }, [carte]);

  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') quandFermer();
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [quandFermer]);

  if (carte === null) return null;
  const d = carte.definition;
  const unite =
    d.unite === 'pourcent'
      ? 'en %'
      : d.unite === 'euros'
        ? 'en €'
        : d.unite === 'euro_km'
          ? 'en €/km'
          : d.unite === 'jours'
            ? 'en jours'
            : d.unite === 'km'
              ? 'en km'
              : '';

  return (
    <div className="ech-voile bfr-voile" role="presentation" onClick={quandFermer}>
      <div
        className="bfr-fenetre"
        role="dialog"
        aria-modal="true"
        aria-label={d.nom}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ech-panneau-tete">
          <div>
            <h2>{d.nom}</h2>
            <p className="ech-sous">
              {periodeEnMots(carte.lue.periode)} ·{' '}
              {d.rythme === 'mois' ? 'mensuel' : 'hebdomadaire'} ·{' '}
              {d.sens === 'haut' ? 'plus haut est mieux' : 'plus bas est mieux'}
              {d.ecran !== undefined && ` · rappel de l’écran ${d.ecran}`}
            </p>
          </div>
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={quandFermer}
          >
            ×
          </button>
        </div>
        <p className="bfr-calcul">
          <strong>{ecrire(carte.lue.valeur, d.unite)}</strong> ·{' '}
          {NOMS_STATUTS[carte.statut]} · {d.explication}
          {carte.lue.manque !== null && ` · ${carte.lue.manque}`}
        </p>
        {carte.lue.serie.length > 0 && (
          <CourbeAnalyse
            titre="Douze dernières périodes"
            sousTitre={carte.lue.sous_ligne ?? ''}
            etiquettes={carte.lue.serie.map((s) => s.periode)}
            series={[
              { nom: d.nom, teinte: 1, valeurs: carte.lue.serie.map((s) => s.valeur) },
            ]}
            seuils={[
              ...(carte.lue.objectif === null
                ? []
                : [{ valeur: carte.lue.objectif, nom: 'objectif' }]),
              ...(carte.lue.alerte === null
                ? []
                : [{ valeur: carte.lue.alerte, nom: 'alerte' }]),
            ]}
            courant={carte.lue.serie.length - 1}
            futurDepuis={carte.lue.serie.length}
            format={(v) => ecrire(v, d.unite)}
            hauteur={300}
          />
        )}
        <form
          className="treso-regle"
          onSubmit={(e) => {
            e.preventDefault();
            const o = versSeuil(objectif, d.unite);
            const a = versSeuil(alerte, d.unite);
            if (o === 'faux' || a === 'faux') {
              setMessage('Un nombre, ou rien.');
              return;
            }
            void actions.poserSeuils(d.cle, o, a).then(setMessage);
          }}
        >
          <label className="ech-champ">
            Objectif {unite}
            <input
              inputMode="decimal"
              value={objectif}
              onChange={(e) => setObjectif(e.target.value)}
            />
          </label>
          <label className="ech-champ">
            Seuil d’alerte {unite}
            <input
              inputMode="decimal"
              value={alerte}
              onChange={(e) => setAlerte(e.target.value)}
            />
          </label>
          <button type="submit" className="bouton bouton-petit">
            Enregistrer
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            onClick={() => {
              setObjectif('');
              setAlerte('');
              void actions.poserSeuils(d.cle, null, null).then(setMessage);
            }}
          >
            Retirer les seuils
          </button>
          {message !== null && <span className="ech-ko">{message}</span>}
        </form>
        <p className="ech-petit ech-doux">
          {d.sens === 'haut'
            ? 'Plus haut est mieux : le seuil d’alerte se met SOUS l’objectif.'
            : 'Plus bas est mieux : le seuil d’alerte se met AU-DESSUS de l’objectif.'}{' '}
          Rien n’est proposé : sans seuil, la carte est mesurée mais pas jugée.
        </p>
      </div>
    </div>
  );
}
